import { useDateTimeDisplay } from '../../../util/useDateTimeDisplay';
import CalendarDateField from '../../../util/CalendarDateField';
import ReminderDayOptions, { DEFAULT_REMINDER_DAYS, reminderSummary } from './ReminderDayOptions';
/**
 * DocumentsTab.jsx
 *
 * Reusable documents UI for resident/staff documents.
 */

import React from "react";
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    Stack,
    CircularProgress,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListSubheader,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert,
    TextField,
    Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";


const isExpired = (doc) => {
    if (!doc?.expiresAt) return false;
    const expiry = new Date(doc.expiresAt);
    if (Number.isNaN(expiry.getTime())) return false;
    return expiry < new Date();
};

export default function DocumentsTab({
    title,
    documentTypes,
    documents,
    docType,
    onChangeDocType,
    customTypeName,
    onChangeCustomTypeName,
    expiresAt,
    onChangeExpiresAt,
    reminderDays = DEFAULT_REMINDER_DAYS,
    onChangeReminderDays,
    onUpdateReminders,
    uploading,
    onSelectFile,
    onFileSelected,
    onDelete,
    fileInputRef,
    groupByType = false,
    emptyText = "No documents uploaded yet.",
    showTypeInSecondary = false,
    typeLabelMap = {},
    isDisabled = false,
    remindersDisabled = isDisabled
}) {
    const { dateLabel: formatDate, displayCalendar } = useDateTimeDisplay();
    const [expiryValid, setExpiryValid] = React.useState(true);
    const [editingDocument, setEditingDocument] = React.useState(null);
    const [editedDays, setEditedDays] = React.useState(DEFAULT_REMINDER_DAYS);
    const [savingReminders, setSavingReminders] = React.useState(false);
    const [reminderError, setReminderError] = React.useState('');
    const editReminders = doc => {
        setEditingDocument(doc);
        setEditedDays(doc.reminderDays ?? DEFAULT_REMINDER_DAYS);
        setReminderError('');
    };
    const saveReminders = async () => {
        setSavingReminders(true); setReminderError('');
        try { await onUpdateReminders(editingDocument.id, editedDays); setEditingDocument(null); }
        catch (error) { setReminderError(error.message || 'Could not save reminders. Please try again.'); }
        finally { setSavingReminders(false); }
    };
    const documentsByType = groupByType
        ? documents.reduce((acc, doc) => {
            const key = doc.type || "OTHER";
            if (!acc[key]) acc[key] = [];
            acc[key].push(doc);
            return acc;
        }, {})
        : null;

    const orderedTypes = groupByType
        ? documentTypes.map((t) => t.value).filter((type) => documentsByType[type]?.length)
        : [];
    const extraTypes = groupByType
        ? Object.keys(documentsByType).filter((type) => !orderedTypes.includes(type))
        : [];
    const groupedTypes = groupByType ? [...orderedTypes, ...extraTypes] : [];

    const buildSecondary = (doc) => {
        const parts = [];
        if (showTypeInSecondary) {
            parts.push(typeLabelMap[doc.type] || doc.type || "Other");
        }
        parts.push(`Uploaded ${formatDate(doc.createdAt)}`);
        if (doc.expiresAt) {
            parts.push(`Expires ${formatDate(doc.expiresAt)}`);
        }
        return parts.join(" • ");
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {title}
            </Typography>

            <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={onFileSelected}
            />

            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mb: 3 }}
                alignItems={{ xs: "stretch", sm: "center" }}
            >
                <FormControl
                    size="small"
                    fullWidth
                    sx={{ minWidth: { sm: 200 } }}
                >
                    <InputLabel>Document Type</InputLabel>
                    <Select
                        label="Document Type"
                        value={docType}
                        disabled={isDisabled}
                        sx={{ fontSize: '0.8125rem', fontWeight: 400 }}
                        MenuProps={{
                            PaperProps: { sx: { maxHeight: 400, borderRadius: 2, border: '1px solid', borderColor: 'divider' } },
                            MenuListProps: { sx: { py: 0.75 } },
                        }}
                        onChange={(e) => onChangeDocType(e.target.value)}
                    >
                        {documentTypes.flatMap((opt, index) => [
                            ...(opt.category && opt.category !== documentTypes[index - 1]?.category
                              ? [<ListSubheader key={`category-${opt.category}`} sx={{ bgcolor: 'background.paper', color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, lineHeight: '30px', px: 2, mt: index ? 0.75 : 0 }}>{opt.category}</ListSubheader>]
                              : []),
                            <MenuItem key={opt.value} value={opt.value}
                                sx={{ pl: opt.category ? 3.5 : 2, pr: 2, py: 0.75, minHeight: { xs: 44, sm: 34 }, fontSize: '0.8125rem', fontWeight: 400 }}>
                                {opt.label}
                            </MenuItem>,
                        ])}
                    </Select>
                </FormControl>

                {docType === "OTHER" && (
                    <TextField
                        fullWidth
                        size="small"
                        label="Custom document type"
                        placeholder="e.g. Equipment warranty"
                        disabled={isDisabled}
                        value={customTypeName}
                        onChange={(e) => onChangeCustomTypeName(e.target.value)}
                    />
                )}

                <CalendarDateField label="Expires On" disabled={isDisabled} value={expiresAt} onChange={onChangeExpiresAt} calendar={displayCalendar} onValidityChange={setExpiryValid} />

                <Button
                    variant="outlined"
                    onClick={onSelectFile}
                    disabled={uploading || isDisabled || !expiryValid}
                    sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
                >
                    Select File
                </Button>

                {uploading && <CircularProgress size={24} />}
            </Stack>

            {onChangeReminderDays && <Box sx={{ mt: 2 }}><ReminderDayOptions value={reminderDays} onChange={onChangeReminderDays} disabled={isDisabled || uploading} /></Box>}

            <Dialog open={Boolean(editingDocument)} onClose={() => !savingReminders && setEditingDocument(null)} fullWidth maxWidth="sm">
                <DialogTitle>Document reminders</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 3, overflowWrap: 'anywhere' }}>{editingDocument?.fileName || editingDocument?.type}</Typography>
                    <ReminderDayOptions value={editedDays} onChange={setEditedDays} disabled={savingReminders} />
                    {!editingDocument?.expiresAt && <Alert severity="info" sx={{ mt: 2 }}>This document has no expiry date, so reminders will not be sent.</Alert>}
                    {reminderError && <Alert severity="error" sx={{ mt: 2 }}>{reminderError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button disabled={savingReminders} onClick={() => setEditingDocument(null)}>Cancel</Button>
                    <Button variant="contained" disabled={savingReminders || remindersDisabled} onClick={saveReminders}>{savingReminders ? 'Saving…' : 'Save reminders'}</Button>
                </DialogActions>
            </Dialog>

            {documents.length === 0 && (
                <Typography color="text.secondary">
                    {emptyText}
                </Typography>
            )}

            {groupByType ? (
                <Stack spacing={2}>
                    {groupedTypes.map((type) => (
                        <Box key={type}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                            >
                                {typeLabelMap[type] || type}
                            </Typography>
                            <List>
                                {documentsByType[type].map((doc) => (
                                    <ListItem
                                        key={doc.id}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                color="error"
                                                disabled={isDisabled}
                                                onClick={() => onDelete(doc.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <a
                                                        href={doc.fileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {doc.fileName}
                                                    </a>
                                                    {isExpired(doc) && (
                                                        <Chip
                                                            label="Expired"
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                            secondaryTypographyProps={{ component: 'div' }}
                                            secondary={<Box><Typography variant="body2" color="text.secondary">{buildSecondary(doc)}</Typography>
                                                <Typography variant="caption" color="text.secondary">{reminderSummary(doc.reminderDays)}{!doc.expiresAt ? ' ? No expiry date' : ''}</Typography>
                                                {onUpdateReminders && !remindersDisabled && <Button size="small" sx={{ display: 'block', px: 0, fontSize: '0.75rem' }} onClick={() => editReminders(doc)}>Change reminders</Button>}
                                            </Box>}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <List>
                    {documents.map((doc) => (
                        <ListItem
                            key={doc.id}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    color="error"
                                    disabled={isDisabled}
                                    onClick={() => onDelete(doc.id)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            }
                        >
                            <ListItemText
                                primary={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <a
                                            href={doc.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {doc.fileName}
                                        </a>
                                        {isExpired(doc) && (
                                            <Chip
                                                label="Expired"
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                            />
                                        )}
                                    </Stack>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                                            secondary={<Box><Typography variant="body2" color="text.secondary">{buildSecondary(doc)}</Typography>
                                                <Typography variant="caption" color="text.secondary">{reminderSummary(doc.reminderDays)}{!doc.expiresAt ? ' ? No expiry date' : ''}</Typography>
                                                {onUpdateReminders && !remindersDisabled && <Button size="small" sx={{ display: 'block', px: 0, fontSize: '0.75rem' }} onClick={() => editReminders(doc)}>Change reminders</Button>}
                                            </Box>}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
}
